import { ClientOnly } from '@/components/ClientOnly';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { type ButtonHTMLAttributes, type ForwardedRef, forwardRef } from 'react';

interface Props extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'link' | 'secondary';
  size?: 'small' | 'normal' | 'large' | 'xlarge';
  fontWeight?: 'normal' | 'bold';
  iconPrefix?: IconProp;
  prefixClassName?: string;
  borderless?: boolean;
}

export default forwardRef(Button);
function Button(
  { variant, size = 'normal', fontWeight = 'bold', iconPrefix, prefixClassName, children, borderless, ...props }: Props,
  ref: ForwardedRef<HTMLButtonElement>,
) {
  return (
    <button
      ref={ref}
      {...props}
      className={classNames(
        'flex flex-row gap-2 items-center rounded-md outline-none',
        {
          'justify-center': !classNames(props.className).includes('justify'),
          'bg-neutral-100 text-white border': variant === 'primary',
          'border-neutral-500': (variant === 'primary' || !variant) && !borderless,
          'border-transparent': borderless,
          'bg-secondary-50': variant === 'secondary',
          'bg-white text-neutral-100 border': !variant,
          'disabled:opacity-50': true,

          'text-lg leading-6': size === 'large' || size === 'xlarge',
          'text-sm leading-5': size === 'normal' || !size,
          'text-sm leading-4': size === 'small',

          'p-5': variant !== 'link' && size === 'xlarge',
          'p-3': variant !== 'link' && size === 'large',
          'py-2 px-4': variant !== 'link' && (size === 'normal' || !size),
          'py-1 px-2': variant !== 'link' && size === 'small',

          'cursor-pointer': !props.disabled,
          'cursor-default': props.disabled,
          'font-bold': fontWeight === 'bold',
        },
        props.className,
      )}
    >
      {iconPrefix && (
        <ClientOnly>
          <FontAwesomeIcon icon={iconPrefix} className={prefixClassName} />
        </ClientOnly>
      )}
      {children}
    </button>
  );
}
